import {Component, EventEmitter, forwardRef, Input, OnInit, Output} from '@angular/core';
import {TriggerConfiguration} from '@models/adminstration/machine/trigger/trigger-configuration';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormBuilder, UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR, ValidationErrors,
  Validator, Validators
} from '@angular/forms';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {TranslateService} from '@ngx-translate/core';

type ChangeCallbackFn<T> = (value: T) => void;
type TouchCallbackFn = () => void;

@Component({
    selector: 'scitis-admin-opc-offline',
    templateUrl: './opc-offline.component.html',
    styleUrls: ['./opc-offline.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => OpcOfflineComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => OpcOfflineComponent),
            multi: true
        }
    ],
    standalone: false
})
export class OpcOfflineComponent implements ControlValueAccessor, Validator, OnInit {

  @Input()
  cloudPlugs: Gateway[] = [];

  @Input()
  isLoadingCloudPlugs = false;

  @Input()
  isAdminOrHigher: boolean = false;

  @Input()
  submitted: boolean = false;

  @Input()
  triggerConfiguration: TriggerConfiguration = new TriggerConfiguration();

  @Output()
  formIsValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  updatedTriggerConfiguration: EventEmitter<TriggerConfiguration> = new EventEmitter<TriggerConfiguration>();

  triggerConfigOpcOfflineForm: UntypedFormGroup = this.fb.group({});

  INTERVAL_TYPES = ['MINUTES', 'HOURS'];
  translatedIntervalTypes: any[] = new Array<any>();

  constructor(private fb: UntypedFormBuilder,
              private translate: TranslateService) {
  }

// convenience getter for easy access to form fields
  get formControls() {
    return this.triggerConfigOpcOfflineForm.controls;
  }

  ngOnInit(): void {
    this.generateForm();
    this.setTranslatedIntervalTypes();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.triggerConfigOpcOfflineForm.invalid) {
      return {invalidForm: {valid: false, message: 'OpcOfflineComponent fields are invalid'}};
    }
    return null;
  }

  writeValue(val: any): void {
    if (val) {
      this.triggerConfigOpcOfflineForm.setValue(val, {emitEvent: false});
    }
  }

  registerOnChange(fn: ChangeCallbackFn<object>): void {
    this.triggerConfigOpcOfflineForm.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: TouchCallbackFn): void {
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.triggerConfigOpcOfflineForm.disable();
    } else {
      this.triggerConfigOpcOfflineForm.enable();
    }
  }

  onFocusOut() {
    this.onTriggerConfigUpdated();
  }

  private generateForm(): void {
    this.triggerConfigOpcOfflineForm = this.fb.group({
      cloudPlug: new UntypedFormControl(this.triggerConfiguration.cloudplugId, [Validators.required]),
      interval: new UntypedFormControl(this.triggerConfiguration.triggerInterval, [Validators.required, Validators.min(1)]),
      intervalType: new UntypedFormControl(this.triggerConfiguration.triggerIntervalType, Validators.required),
    });

    this.formIsValid.emit(!this.triggerConfigOpcOfflineForm.invalid);
  }

  private onTriggerConfigUpdated() {
    const triggerConfigUpdated = new TriggerConfiguration();
    triggerConfigUpdated.triggerInterval = this.formControls.interval.value;
    triggerConfigUpdated.triggerIntervalType = this.formControls.intervalType.value;
    triggerConfigUpdated.cloudplugId = this.formControls.cloudPlug.value;
    this.updatedTriggerConfiguration.emit(triggerConfigUpdated);
    this.formIsValid.emit(!this.triggerConfigOpcOfflineForm.invalid);
  }

  private setTranslatedIntervalTypes() {
    for (const type of this.INTERVAL_TYPES) {
      this.translatedIntervalTypes = [...this.translatedIntervalTypes, {
        key: type,
        value: this.translate.instant(type)
      }];
    }
  }

}
